import { GetServerSidePropsContext, NextPage } from 'next'
import useHeader from 'src/relay/Header/useHeader'
import { initWithLangAndLabels } from 'src/i18n'
import useFooter from 'src/relay/Footer/useFooter'
import { MeilisearchClient, search } from 'src/meilisearch'
import { Indexes } from 'src/meilisearch/types/Common'
import SearchResultsTemplate from 'src/templates/SearchResultsTemplate'
import { useSearchResults } from 'src/relay/Hooks/useSearchResults'
import useSearchHero from 'src/relay/Hero/useSearchHero'
import useLangSwitch from 'src/relay/LangSwitch/useLangSwitch'
import RestApiClient from 'src/rest/client'
import searchPage from 'src/rest/requests/pages/searchPage'
import { Page } from 'src/rest/types/Page'

import {
  GetServerSidePropsReturnType,
  PageProps,
} from '../../relay/Common/PageProps'
import getConfiguration from '../../rest/services/getConfiguration'
import { sanitizeSSRProps } from '../../utils/sanitizeSSRProps'
import { SeoTransformer } from '../../relay/Transformers/SeoTransformer'

type Props = PageProps & {
  data: Record<Indexes, Array<any>>
  pageData: Page
  q: string
}

const SearchPage: NextPage<Props> = ({
  configuration,
  locale: currentLocale,
  data,
  q,
}) => {
  const otherLocales = configuration?.locales?.length
    ? configuration.locales?.filter((l) => l.code !== currentLocale)
    : []

  const langSwitch = useLangSwitch({
    configuration,
    currentLocale,
    localized: otherLocales.map((locale) => ({
      type: 'search',
      locale: locale.code,
      slug: q,
    })),
  })

  const header = useHeader({
    configuration,
    currentLocale,
    langSwitch,
  })
  const footer = useFooter({ configuration, currentLocale, langSwitch })

  const searchResults = useSearchResults(data, currentLocale, q)
  const hero = useSearchHero({ previousTerms: q, locale: currentLocale })

  return (
    <SearchResultsTemplate
      hero={hero}
      searchResultsProps={searchResults}
      headerProps={header}
      footerProps={footer}
    />
  )
}

export default SearchPage

export async function getServerSideProps(
  context: GetServerSidePropsContext<{ slug: string }>
): GetServerSidePropsReturnType<Props> {
  const locale = context?.locale ?? 'fr'
  const q = context.params?.slug ?? ''

  const [configuration, searchRs, pageRs] = await Promise.all([
    getConfiguration(locale),
    MeilisearchClient.call(search(q, locale)),
    RestApiClient.call(searchPage({ locale })),
  ])

  const data: any = searchRs.data || {}
  const pageData: any = pageRs.data || {}

  const i18n = initWithLangAndLabels(locale, configuration.translations ?? {})

  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=10, stale-while-revalidate=59'
  )

  if (!pageData) {
    return {
      notFound: true,
      props: {
        i18n,
        configuration,
        locale,
      },
    }
  }

  return {
    props: {
      metas: SeoTransformer(context.resolvedUrl, pageData?.seo, locale),
      configuration,
      i18n,
      locale,
      data: sanitizeSSRProps(data) as any,
      q,
      pageData: sanitizeSSRProps(pageData),
    },
  }
}
